import { msg } from '@lingui/core/macro'

import { PreferredCommunicationEnum } from '@pkg/form/common/Favorite'
import { trimToUndefined } from '@pkg/shared/utils'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import { z } from 'zod'

export const ContactInformationSchema = z.object({
  email: z.string().nullish(),
  phone: z.string().nullish(),
  mobile: z.string().nullish(),
  fax: z.string().nullish(),
  preferredCommunication: z.enum(PreferredCommunicationEnum.$.zodEnum()).nullish(),
})

export type ContactInformation = z.infer<typeof ContactInformationSchema>

export interface ContactInformationEntity {
  contact: ContactInformation
}

export function isContactInformationEntity(val: unknown): val is ContactInformationEntity {
  return typeof val === 'object' && val !== null && 'contact' in val
}

export const PostalAddressSchema = z.object({
  streetAddress: z.coerce.string().nullish(),
  zipCode: z.coerce.string().nullish(),
  city: z.string().nullish(),
})

export type PostalAddress = z.infer<typeof PostalAddressSchema>

export const ContactPointSchema = z.object({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  addition: z.string().nullish(),
  comment: z.string().nullish(),
  address: PostalAddressSchema.nullish(),
})

export interface AddressEntity {
  address: PostalAddress
}

export function isAddressEntity(val: unknown): val is AddressEntity {
  return typeof val === 'object' && val !== null && 'address' in val
}

export function isNamedEntity(val: unknown): val is { name: string } {
  return typeof val === 'object' && val !== null && 'name' in val && typeof val.name === 'string'
}

export type ContactPoint = z.infer<typeof ContactPointSchema>

export function isContactPoint(val: unknown): val is ContactPoint {
  return typeof val === 'object' && val !== null && 'firstName' in val && 'lastName' in val && 'address' in val
}

export const SalutationEnum = defineEnum({
  MR: msg`Herr`,
  MRS: msg`Frau`,
})

export type SalutationType = typeof SalutationEnum.$.Type

export const AcademicTitleEnum = defineEnum({
  doctor: msg`Dr.`,
  professor: msg`Prof.`,
  professor_doctor: msg`Prof. Dr.`,
})

export type AcademicTitleType = typeof AcademicTitleEnum.$.Type

export const AddressTypeEnum = defineEnum({
  DELIVERY: msg`Lieferadresse`,
  BILLING: msg`Rechnungsadresse`,
})

export type AddressType = typeof AddressTypeEnum.$.Type

export const isValidPostalAddress = (address?: PostalAddress | null) => {
  if (trimToUndefined(address?.streetAddress)) {
    return trimToUndefined(address?.city) || trimToUndefined(address?.zipCode)
  }
  return false
}

export const isValidContactPoint = (contactPoint?: ContactPoint | null) => {
  return (
    trimToUndefined(contactPoint?.firstName) &&
    trimToUndefined(contactPoint?.lastName) &&
    isValidPostalAddress(contactPoint?.address)
  )
}
